import { Component, OnInit, Renderer2 } from "@angular/core";
import * as FileSaver from "file-saver";
import { ActivatedRoute } from "@angular/router";
import { CommonModule } from "@angular/common";
import { TableModule } from "primeng/table";
import { ReportsService } from "../../../../core/services/reports.service";
import { JniToastrService } from "../../../../core/services/jni-toastr.service";
import { OpenCustomerInvoiceDetailsResponse } from "../../../../core/models/reports/openCustomerInvoiceDetailsResponse";
import { DashboardService } from "../../../../core/services/dashboard.service";
import { LoaderService } from "../../../../core/services/loader.service";
import { DateFilterComponent } from "../../../../shared/components/date-filter/date-filter.component";
import { DateFilter } from "../../../../core/models/shared/dateFilter";

import { ButtonModule } from "primeng/button";
import { CardModule } from "primeng/card";
import { InputTextModule } from "primeng/inputtext";
import { ModalStackerComponent } from "../../../../shared/components/modal-stacker/modal-stacker.component";
import { OrderSummaryComponent } from "../../../orders/components/order-summary/order-summary.component";
import { ClientDetailsComponent } from "../../../clients/components/client-details/client-details.component";

@Component({
  selector: "jni-open-customer-invoices-report",
  templateUrl: "./open-customer-invoices-report.component.html",
  styleUrls: ["./open-customer-invoices-report.component.scss"],
  imports: [
    CommonModule,
    TableModule,
    DateFilterComponent,
    ButtonModule,
    CardModule,
    InputTextModule,
    ModalStackerComponent,
    OrderSummaryComponent,
    ClientDetailsComponent,
  ],
})
export class OpenCustomerInvoicesReportComponent implements OnInit {
  openCustomerInvoiceDetailLists: OpenCustomerInvoiceDetailsResponse[] = [];
  descriptionCharacterCond = false;

  clientId = "";
  orderDisplayModal = false;
  clientDisplayModal = false;
  saleOrderId = "";

  filteredOpenCustInvDetailList: OpenCustomerInvoiceDetailsResponse[] = [];
  dateColumn = "date";
  dueDateColumn = "dueDate";

  constructor(
    private _loaderService: LoaderService,
    private _toastr: JniToastrService,
    private _reportsService: ReportsService,
    private _dashboardService: DashboardService,
    private _aRoute: ActivatedRoute,
    private renderer: Renderer2
  ) {
    this._aRoute.params.subscribe(params => {
      this.clientId = params["customerAccount"];
    });
  }

  ngOnInit(): void {
    this.getOpenCustomerInvoicesReportDetail(this.clientId);
  }

  getOpenCustomerInvoicesReportDetail(clientId: string): void {
    this._loaderService.setLoaderState(true);
    this._reportsService.cusomterInvoicedDetail(clientId).subscribe({
      next: res => {
        this._loaderService.setLoaderState(false);
        if (res.data) {
          this.openCustomerInvoiceDetailLists = res.data;
          this.filteredOpenCustInvDetailList =
            this.openCustomerInvoiceDetailLists;
        } else {
          this._toastr.error(res.message, "Error");
        }
      },
      error: res => {
        this._loaderService.setLoaderState(false);
        this._toastr.error(res.message, "Error");
      },
    });
  }

  exportExcelFile() {
    this.exportExcelForFilterData(this.openCustomerInvoiceDetailLists);
  }
  exportExcelForFilterData(
    filterclientlist: OpenCustomerInvoiceDetailsResponse[]
  ) {
    const object = filterclientlist.map(datum => {
      return {
        ["Invoice#"]: datum.invoiceNumber,
        ["Sales Order"]: datum.salesOrder,
        ["Project Name"]: datum.projectName,
        ["Customer Account"]: datum.customerAccount,
        ["Name"]: datum.name,
        ["Date"]: datum.date,
        ["Due Date"]: datum.dueDate,
        ["Amount In Transaction Currency"]: datum.amountInTransactionCurrency,
        ["Currency"]: datum.currency,
        ["Interest"]: datum.interest,
        ["Payments"]: datum.payments,
        ["Amount"]: datum.amountNotSettled,
        ["Note"]: datum.note,
        ["Invoice Email Address"]: datum.invoiceEmailAddress,
        ["Days Outstanding"]: datum.daysOutstanding,
      };
    });

    import("xlsx").then(xlsx => {
      const worksheet = xlsx.utils.json_to_sheet(object);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer: ArrayBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      this.saveAsExcelFile(excelBuffer, "OpenCustomerInvoices");
    });
  }

  saveAsExcelFile(buffer: ArrayBuffer, fileName: string): void {
    const EXCEL_TYPE =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const EXCEL_EXTENSION = ".xlsx";
    const data: Blob = new Blob([buffer], {
      type: EXCEL_TYPE,
    });
    FileSaver.saveAs(
      data,
      fileName + "_export_" + new Date().getTime() + EXCEL_EXTENSION
    );
  }

  descriptionCharacterCount(obj: string) {
    if (obj.length > 45) this.descriptionCharacterCond = true;
  }

  closeModalForSaleOrder() {
    this.orderDisplayModal = false;
  }

  closeModalForClient() {
    this.clientDisplayModal = false;
  }

  redirectToClient(client: string) {
    if (client) {
      this.clientId = client;
      this.clientDisplayModal = true;
    }
  }

  redirectToSaleorder(saleorder: string) {
    if (saleorder) {
      this.saleOrderId = saleorder;
      this.orderDisplayModal = true;
    }
  }

  getInvoiceReport(invoiceId: string) {
    this._loaderService.setLoaderState(true);
    this._dashboardService.getInvoiceReport(invoiceId).subscribe({
      next: res => {
        this._loaderService.setLoaderState(false);
        if (!res.data) {
          this._toastr.error("Error", res.message);
          return;
        }
        const blob = this.b64toBlob(res.data, "application/pdf");
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl);
      },
      error: () => {
        this._loaderService.setLoaderState(false);
        this._toastr.error("Error", "Failed To Load PDF");
      },
    });
  }

  b64toBlob(b64Data: string, contentType = "", sliceSize = 512) {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
      const slice = byteCharacters.slice(offset, offset + sliceSize);

      const byteNumbers = new Array(slice.length);
      for (let i = 0; i < slice.length; i++) {
        byteNumbers[i] = slice.charCodeAt(i);
      }

      const byteArray = new Uint8Array(byteNumbers);
      byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
  }

  scrollHiddenStatus() {
    setTimeout(() => {
      this.renderer.removeClass(document.body, "p-overflow-hidden");
    }, 0);
  }

  applyFilter($event: OpenCustomerInvoiceDetailsResponse[] | DateFilter) {
    if (Array.isArray($event)) {
      this.filteredOpenCustInvDetailList = $event;
    }
  }

  clearFilter($event: OpenCustomerInvoiceDetailsResponse[] | DateFilter) {
    if (Array.isArray($event)) {
      this.filteredOpenCustInvDetailList = $event;
    }
  }
}
